import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { StayCalendar, StayCalendarReservation } from '../../../../interfaces/stays';
import { FormModal } from '../../../modal/form-modal';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { formatDayKey } from '../../../../function-utils/building-time';
import { palette, radius, spacing, typography } from '../../../../style-utils/theme';
import {
  CalendarIndex,
  PROVIDER_TEXT,
  blockText,
  departureText,
  firstNightText,
  footerOf,
  gapText,
  guestName,
  laterNightText,
  rolesOf,
} from './calendar-model';

/**
 * THE DAY SHEET — what one day holds, as a list a thumb can use (plan S4.10).
 * Opened by a tap on a phone, by Space on a day anywhere, and by a click on a
 * day when the reader cannot manage dates. With stays:manage it carries the
 * day's actions: "Open stay", "Close dates from here…", "Re-open this date".
 */

const Items = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

const ItemText = styled.span`
  flex: 1 1 ${spacing.huge};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Quiet = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin-top: ${spacing.sm};
`;

export interface DaySheetProps {
  /** The day on show; null closes the sheet. */
  day: string | null;
  data: StayCalendar;
  index: CalendarIndex;
  canManage: boolean;
  onClose: () => void;
  onOpenStay: (id: string) => void;
  /** "Close dates from here…": start a selection on this day. */
  onCloseFrom: (day: string) => void;
  /** "Re-open this date": the parent re-opens [day, day + 1). */
  onReopenDay: (day: string) => Promise<void>;
}

export const DaySheet: React.FC<DaySheetProps> = ({ day, data, index, canManage, onClose, onOpenStay, onCloseFrom, onReopenDay }) => {
  const [reopening, setReopening] = useState(false);
  const roles = day ? rolesOf(index, day) : null;
  const title = day ? formatDayKey(day, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Day';

  const stayRow = (stay: StayCalendarReservation, text: string, key: string) => (
    <Item key={key} data-stay-id={stay.id}>
      <ItemText>
        {text}
        {stay.access_revoked ? ' · Guest access revoked' : ''}
      </ItemText>
      {canManage ? (
        <AppButton variant="secondary" aria-label={`Open stay — ${guestName(stay)}`} onClick={() => onOpenStay(stay.id)}>
          Open stay
        </AppButton>
      ) : null}
    </Item>
  );

  const footer = roles ? footerOf(roles, index, data) : null;
  const empty = !!roles && !roles.night && !roles.departure && !roles.block && !roles.provider && !roles.gap;

  return (
    <FormModal open={!!day} title={title} subtitle={day === data.building_today ? 'Today' : undefined} onClose={onClose}>
      {day && roles ? (
        <div data-day-sheet={day}>
          {empty ? (
            <Quiet>Nothing is booked or closed on this day.</Quiet>
          ) : (
            <Items>
              {roles.departure ? stayRow(roles.departure, departureText(roles.departure), 'departure') : null}
              {roles.night
                ? stayRow(roles.night.stay, roles.night.first ? firstNightText(roles.night.stay) : laterNightText(roles.night), 'night')
                : null}
              {roles.block ? (
                <Item key="block" data-block-id={roles.block.id}>
                  <ItemText>{blockText(roles.block)}</ItemText>
                </Item>
              ) : null}
              {roles.provider ? (
                <Item key="provider" data-block-id={roles.provider.id}>
                  <ItemText>{PROVIDER_TEXT}</ItemText>
                </Item>
              ) : null}
              {roles.gap ? (
                <Item key="gap">
                  <StatusPill tone={roles.gap.nights === 1 ? 'warning' : 'neutral'} data-gap={roles.gap.nights}>
                    {gapText(roles.gap.nights)}
                  </StatusPill>
                </Item>
              ) : null}
              {footer ? (
                <Item key="footer">
                  <ItemText>{footer.text}</ItemText>
                </Item>
              ) : null}
            </Items>
          )}
          {canManage ? (
            <Actions>
              {!roles.night && !roles.block ? (
                <AppButton variant="primary" onClick={() => onCloseFrom(day as string)}>
                  Close dates from here…
                </AppButton>
              ) : null}
              {roles.block ? (
                <AppButton
                  variant="secondary"
                  loading={reopening}
                  loadingLabel="Re-opening"
                  onClick={async () => {
                    setReopening(true);
                    try {
                      await onReopenDay(day as string);
                    } finally {
                      setReopening(false);
                    }
                  }}
                >
                  Re-open this date
                </AppButton>
              ) : null}
            </Actions>
          ) : null}
        </div>
      ) : null}
    </FormModal>
  );
};
